import type {
	ExerciseInfoId,
	MetricCanonicalName,
	SectionPlanInfoId,
	StepPlanInfo,
	StepPlanInfoId,
} from '@az3fr/core';
import { createID } from '@az3fr/core';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

const DEFAULT_MAX_TRACKED_METRICS = 3;

interface CreateStepPlanObjectPayload {
	exerciseId: ExerciseInfoId;
	sectionPlanId: SectionPlanInfoId;
	orderInSectionPlan: number;
}
/**
 * Creates a complete StepPlanInfo object from payload data
 * This function can be reused anywhere in the codebase where we need to create step plans
 */
function createNewStepPlan({
	exerciseId,
	sectionPlanId,
	orderInSectionPlan,
}: CreateStepPlanObjectPayload): StepPlanInfo {
	const now = new Date().toISOString();
	return {
		id: createID('stepPlan'),
		notes: null,
		exerciseId,
		exerciseComplexId: null,
		sectionPlanId,
		orderInSectionPlan,
		repetitionMode: 'template',
		// We initialize this to null, the calling code should set it to number for standard sections
		templateSets: null,
		trackedMetrics: ['weight', 'reps'],
		version: 0,
		updatedAt: now,
	};
}

const stepPlanAdapter = createEntityAdapter<StepPlanInfo>({
	sortComparer: (a, b) => a.orderInSectionPlan - b.orderInSectionPlan,
});
const stepPlanSlice = createSlice({
	name: 'entities/stepPlan',
	initialState: stepPlanAdapter.getInitialState(),
	reducers: {
		metricTracked: (
			state,
			action: PayloadAction<{
				id: StepPlanInfoId;
				metricCanonicalName: MetricCanonicalName;
			}>,
		) => {
			const stepPlan = state.entities[action.payload.id];
			if (!stepPlan) {
				return;
			}

			const { trackedMetrics } = stepPlan;
			const newMetric = action.payload.metricCanonicalName;

			// Don't add if already tracked or would exceed limit
			if (
				trackedMetrics.includes(newMetric) ||
				trackedMetrics.length >= DEFAULT_MAX_TRACKED_METRICS
			) {
				return;
			}

			stepPlanAdapter.updateOne(state, {
				id: action.payload.id,
				changes: { trackedMetrics: [...trackedMetrics, newMetric] },
			});
		},

		metricUntracked: (
			state,
			action: PayloadAction<{
				id: StepPlanInfoId;
				metricCanonicalName: MetricCanonicalName;
			}>,
		) => {
			const { id, metricCanonicalName } = action.payload;
			const stepPlan = state.entities[id];
			if (!stepPlan) {
				return;
			}

			const { trackedMetrics } = stepPlan;

			stepPlanAdapter.updateOne(state, {
				id,
				changes: {
					trackedMetrics: trackedMetrics.filter(
						(metric: MetricCanonicalName) => metric !== metricCanonicalName,
					),
				},
			});
		},

		addedOne: stepPlanAdapter.addOne,
		addedMany: stepPlanAdapter.addMany,

		updatedOne: stepPlanAdapter.updateOne,
		updatedMany: stepPlanAdapter.updateMany,

		removedOne: stepPlanAdapter.removeOne,
		removedMany: stepPlanAdapter.removeMany,
	},
});
type StepPlanSliceState = ReturnType<typeof stepPlanAdapter.getInitialState>;

const stepPlanSliceActions = { ...stepPlanSlice.actions };
const stepPlanSliceReducer = stepPlanSlice.reducer;

export {
	createNewStepPlan,
	DEFAULT_MAX_TRACKED_METRICS,
	stepPlanSliceActions,
	stepPlanSliceReducer,
	stepPlanAdapter,
};
export type { StepPlanSliceState };
